import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { pageApi } from '../services/api';

const AppLayout = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();

  // Check if current route is a page view
  const isPageView = location.pathname.startsWith('/page/');

  useEffect(() => {
    if (isAuthenticated) {
      loadPages();
    }

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, [isAuthenticated]);

  const loadPages = async () => {
    try {
      const data = await pageApi.getAll();
      setPages(data?.data || []);
    } catch (error) {
      console.error('Failed to load pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePage = async (title = 'Untitled') => {
    try {
      const newPage = await pageApi.create({
        title,
        icon: '📄'
      });

      loadPages();
      return newPage.data.slug;
    } catch (error) {
      console.error('Failed to create page:', error);
      return null;
    }
  };

  const handleDeletePage = async (pageId) => {
    if (window.confirm('Are you sure you want to delete this page?')) {
      try {
        await pageApi.delete(pageId);
        loadPages();
      } catch (error) {
        console.error('Failed to delete page:', error);
        alert('Failed to delete page. Please try again.');
      }
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
    }
  };

  // Refresh pages list - can be called from child components
  const handleRefreshPages = async () => {
    await loadPages();
  };

  // Don't show sidebar for public page views
  if (!isAuthenticated && isPageView) {
    return (
      <div className="app">
        <main className="main-content full-width">
          <Outlet />
        </main>
      </div>
    );
  }

  // Show sidebar for authenticated users
  return (
    <div className="app">
      {isAuthenticated && (
        <>
          <Sidebar
            pages={pages}
            user={user}
            onCreatePage={handleCreatePage}
            onDeletePage={handleDeletePage}
            onRefreshPages={handleRefreshPages}
            onLogout={handleLogout}
            isOpen={sidebarOpen}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
          />

          {isMobile && sidebarOpen && (
            <div
              className="sidebar-overlay"
              onClick={() => setSidebarOpen(false)}
              style={{
                display: isMobile && sidebarOpen ? 'block' : 'none'
              }}
            />
          )}
        </>
      )}

      <main className={`main-content ${sidebarOpen && !isMobile && isAuthenticated ? 'sidebar-open' : 'full-width'}`}>
        <Outlet />
      </main>
    </div>
  );
};

export default AppLayout;